import {
	type Field,
	type FormValues,
	type StepI,
	type WizardFormConfig,
	wizardFormSchema,
} from "@CommonLib/src/frontend/WizardForm/types";
import { hasDuplicates } from "@CommonLib/src/utils/arrays";
import {
	ensureDefined,
	ensureNotNull,
	typedValues,
} from "@CommonLib/src/utils/type_guards";

export const generateIdentifier = (
	stepId: number,
	fieldId: number,
	choiceId: number,
) => {
	return `${stepId}_${fieldId}_${choiceId}`;
};

export const getChoicesFromField = (field: Field) => {
	const choices = field.choices ?? [];
	// We dont want to mutate choices array in place because unit tests ensure choices are in wrong
	// order in config object before ensuring they are in right order in rendered HTML
	const choicesNewRef = [...choices];
	return choicesNewRef.sort((aa, bb) => aa.index - bb.index);
};

export const getRootStep = (steps: StepI[]) => {
	return steps.filter((step) => step.parentStepId === null)[0];
};

export const findChildren = (steps: StepI[], stepId: number) => {
	const childCandidates = steps.filter((step) => step.parentStepId === stepId);
	return childCandidates;
};

export const getStepFromId = (
	config: Omit<WizardFormConfig, "configId">,
	stepId: number,
) => {
	return ensureDefined(
		config.steps.filter((step) => step.id === stepId)[0],
		"Step not found",
	);
};
export const getFieldFromId = (
	config: Omit<WizardFormConfig, "configId">,
	stepId: number,
	fieldId: number,
) => {
	const step = getStepFromId(config, stepId);
	return ensureDefined(
		step.fields.filter((field) => field.id === fieldId)[0],
		"Field not found",
	);
};

export const getChoiceFromId = (
	config: Omit<WizardFormConfig, "configId">,
	stepId: number,
	fieldId: number,
	choiceId: number,
) => {
	const field = getFieldFromId(config, stepId, fieldId);
	return ensureDefined(
		(field.choices ?? []).filter((choice) => choice.id === choiceId)[0],
		"Choice not found",
	);
};

export const fieldsFromStepId = (
	config: Omit<WizardFormConfig, "configId">,
	stepId: number,
) => {
	const step = getStepFromId(config, stepId);
	// We dont want to mutate fields array in place because unit tests ensure fields are in wrong
	// order in config object before ensuring they are in right order in rendered HTML
	const fieldsNewRef = [...step.fields];
	return fieldsNewRef.sort((aa, bb) => aa.index - bb.index);
};

export const stepsUsingStepAsCondition = (
	config: Omit<WizardFormConfig, "configId">,
	stepId: number,
) => {
	return config.steps.filter(
		(s) => s.triggerChoice && s.triggerChoice.stepId === stepId,
	);
};

export const getSiblings = (
	config: Omit<WizardFormConfig, "configId">,
	parentStepId: number,
	stepId: number | null,
) => {
	return stepsUsingStepAsCondition(config, parentStepId).filter(
		(s) => s.id !== stepId,
	);
};

export const stepsUsingFieldAsCondition = (
	config: Omit<WizardFormConfig, "configId">,
	stepId: number,
	fieldId: number,
) => {
	return config.steps
		.filter(
			(s) =>
				s.triggerChoice &&
				s.triggerChoice.stepId === stepId &&
				s.triggerChoice.fieldId === fieldId,
		)
		.map((s) => s.name);
};

export const stepsUsingChoiceAsCondition = (
	config: Omit<WizardFormConfig, "configId">,
	stepId: number,
	fieldId: number,
	choiceId: number,
) => {
	return config.steps
		.filter(
			(s) =>
				s.triggerChoice &&
				s.triggerChoice.stepId === stepId &&
				s.triggerChoice.fieldId === fieldId &&
				s.triggerChoice.choiceId === choiceId,
		)
		.map((s) => s.name);
};

export const getFieldSource = (stepId: number, fieldIdentifier: number) => {
	return `${stepId.toString()}_${fieldIdentifier.toString()}`;
};

export const getFieldFromFieldSource = (
	config: Omit<WizardFormConfig, "configId">,
	fieldSource: string,
) => {
	const [stepId, fieldIdentifier] = fieldSource.split("_");
	if (stepId === undefined) throw Error();
	if (fieldIdentifier === undefined) throw Error();
	const step = getStepFromId(config, Number(stepId));
	const field = step.fields.filter(
		(field) => field.identifier.toString() === fieldIdentifier,
	)[0];
	if (!field) throw Error();
	return field;
};

function isZeroBasedSequence(arr: number[]): boolean {
	const arrSorted = [...arr];
	arrSorted.sort((a, b) => a - b);
	return arrSorted.every((val, i) => val === i);
}

export const rule1ErrorMsg = "All step ids should be unique";
export const rule2ErrorMsg =
	"All field ids and identifiers should be unique within a given step";
export const rule3ErrorMsg =
	"All choice ids should be unique within a given field";
export const rule4ErrorMsg = "Only 1 step has parentStepId = null";
export const rule5ErrorMsg =
	"Step triggerChoice does not point to a step/field/choice from parent step";
export const rule6ErrorMsg =
	"A choice id is used as triggerChoiceId by multiple steps";
export const rule7ErrorMsg =
	"A step cannot have multiple child steps with triggerChoiceId set to null";
export const rule8ErrorMsg =
	"If one child step has triggerChoiceId specified, all child steps must have this field specified";
export const rule9ErrorMsg =
	"For any given step, all children steps with triggerChoice must have triggerChoices relating to the same field";
export const rule10ErrorMsg =
	"A selectInput referenced by condition among step children has to be required (required: true) in the config json";
export const rule11ErrorMsg =
	"Fields indices in a given step must be unique, incrementing by 1 and start from 0";
export const rule12ErrorMsg =
	"Choices indices in a given field must be unique, incrementing by 1 and start from 0";

export const lintWizardFormConfig = (
	config: Omit<WizardFormConfig, "configId">,
) => {
	// First we validate the structure
	const configValidated = wizardFormSchema
		.omit({ configId: true })
		.parse(config);
	const allSteps = configValidated.steps;

	// Rule 1: ensure all step ids are unique
	const stepIds: number[] = configValidated.steps.map((step) => step.id);
	const stepIdsWithParentStepIdNull: number[] = [];

	let idx = 0;
	for (const step of allSteps) {
		if (step.parentStepId === null) {
			stepIdsWithParentStepIdNull.push(step.id);
		}
		const fieldIds: number[] = [];
		const fieldIdentifiers: number[] = [];
		for (const field of step.fields) {
			fieldIds.push(field.id);
			fieldIdentifiers.push(field.identifier);
			const choiceIds: number[] = [];
			if (field.type === "select") {
				const choicesTyped = ensureNotNull(
					field.choices,
					"choices cant be null for a select field",
				);
				for (const choice of choicesTyped) {
					choiceIds.push(choice.id);
				}
				// Rule 3: ensure all choice ids are unique
				if (hasDuplicates(choiceIds)) throw Error(rule3ErrorMsg);
			}
		}
		// Rule 2: ensure all field ids are unique within a given step
		if (hasDuplicates(fieldIds)) throw Error(rule2ErrorMsg);
		if (hasDuplicates(fieldIdentifiers)) throw Error(rule2ErrorMsg);

		idx = idx + 1;
	}
	if (hasDuplicates(stepIds)) throw Error(rule1ErrorMsg);

	// Rule 4: ensure only 1 step has parentStepId null - we accept 0 (if form has no steps)
	if (stepIdsWithParentStepIdNull.length > 1) throw Error(rule4ErrorMsg);

	// Rule 5: ensure for each step that if triggerChoiceId is defined, it points to a choice in a field of parent step
	for (const step of allSteps) {
		const triggerChoice = step.triggerChoice;
		if (triggerChoice !== null) {
			const parentStepId = ensureNotNull(
				step.parentStepId,
				"A step with triggerChoice cannot have no parent",
			);
			const parentStep = ensureDefined(
				configValidated.steps.filter((step) => step.id === parentStepId)[0],
				"Parent step id is defined so parent step must exist",
			);
			// We ensure triggerChoice.stepId = parent step id
			if (triggerChoice.stepId !== step.parentStepId) {
				throw Error(rule5ErrorMsg);
			}

			// We ensure triggerChoice.fieldId = field from parent step
			const parentStepFieldIds = parentStep.fields.map((f) => f.id);
			if (!parentStepFieldIds.includes(triggerChoice.fieldId))
				throw Error(rule5ErrorMsg);

			const targetField = fieldsFromStepId(config, triggerChoice.stepId).filter(
				(f) => f.id === triggerChoice.fieldId,
			)[0];
			if (!targetField) throw Error();
			const targetChoiceIds = (targetField.choices ?? []).map((c) => c.id);
			if (!targetChoiceIds.includes(triggerChoice.choiceId))
				throw Error(rule5ErrorMsg);
		}
	}

	// Rule 6: ensure no steps share a triggerChoiceId
	const triggerChoiceIds: Record<string, number[]> = {};
	for (const step of allSteps) {
		const triggerChoice = step.triggerChoice;
		if (triggerChoice !== null) {
			const identifier = generateIdentifier(
				triggerChoice.stepId,
				triggerChoice.fieldId,
				triggerChoice.choiceId,
			);
			if (triggerChoiceIds[identifier] === undefined)
				triggerChoiceIds[identifier] = [];
			triggerChoiceIds[identifier]?.push(step.id);
		}
	}
	for (const triggerChoiceIdStepIds of typedValues(triggerChoiceIds)) {
		if (triggerChoiceIdStepIds.length > 1) throw Error(rule6ErrorMsg);
	}

	// Rule 7: for any given step, there can be only one child step with triggerChoiceId set to null
	for (const step of allSteps) {
		const childSteps = findChildren(configValidated.steps, step.id);
		const childStepsWithNullTriggerChoiceId = childSteps.filter(
			(step) => step.triggerChoice === null,
		);
		if (childStepsWithNullTriggerChoiceId.length > 1)
			throw Error(rule7ErrorMsg);
	}

	// Rule 8: for any given step, if one child step has triggerChoiceId specified, they must all have this field specified
	for (const step of allSteps) {
		const childSteps = findChildren(configValidated.steps, step.id);
		const childStepsWithTriggerChoiceId = childSteps.filter(
			(step) => step.triggerChoice !== null,
		);
		if (
			childStepsWithTriggerChoiceId.length !== 0 &&
			childStepsWithTriggerChoiceId.length !== childSteps.length
		)
			throw Error(rule8ErrorMsg);
	}

	// Rule 9: for any given step, all children steps with triggerChoiceId must have triggerChoiceIds relating to the same field
	// Rule 10: a selectInput referenced by condition among step children has to be required (required: true) in the config json
	for (const step of allSteps) {
		const childSteps = findChildren(configValidated.steps, step.id);
		const childStepsTriggerChoices = childSteps
			.map((step) => step.triggerChoice)
			.filter((id) => id !== null);

		const firstTriggerChoice = childStepsTriggerChoices[0];
		if (firstTriggerChoice !== undefined) {
			const selectFields = step.fields.filter(
				(field) => field.type === "select",
			);
			for (const field of selectFields) {
				const choiceIds = ensureNotNull(
					field.choices,
					".choices must be defined for a select field",
				).map((choice) => choice.id);
				// if the first triggerChoice is tied to this field, then all others must be tied too to this field
				if (firstTriggerChoice.fieldId === field.id) {
					for (const triggerChoice of childStepsTriggerChoices) {
						if (triggerChoice.fieldId !== field.id) throw Error(rule9ErrorMsg);
					}
					// If the first triggerChoice is tied to this field choices, must be the same for other triggerChoices
					if (choiceIds.includes(firstTriggerChoice.choiceId)) {
						if (!field.required) throw Error(rule10ErrorMsg);
						for (const triggerChoice of childStepsTriggerChoices) {
							if (!choiceIds.includes(triggerChoice.choiceId))
								throw Error(rule9ErrorMsg);
						}
					}
				}
			}
		}
	}

	// Rule 11 and rule 12
	for (const step of allSteps) {
		const fieldIndices = step.fields.map((field) => field.index);
		if (hasDuplicates(fieldIndices)) throw Error(rule11ErrorMsg);
		if (!isZeroBasedSequence(fieldIndices)) throw Error(rule11ErrorMsg);
		for (const field of step.fields) {
			const choices = field.choices;
			if (choices) {
				const choicesIndices = choices.map((choice) => choice.index);
				if (hasDuplicates(choicesIndices)) throw Error(rule12ErrorMsg);
				if (!isZeroBasedSequence(choicesIndices)) throw Error(rule12ErrorMsg);
			}
		}
	}
};

export const countTotalFieldsInPath = (
	config: WizardFormConfig,
	stepHistory: number[],
	formValues: FormValues,
	currentStepId: number,
): number => {
	let total = 0;

	// 1. Sum fields for all visited steps in stepHistory
	for (const visitedStepId of stepHistory) {
		total += fieldsFromStepId(config, visitedStepId).length;
	}

	// 2. Project forward from current step
	let projStepId: number | undefined = currentStepId;
	while (projStepId !== undefined) {
		const children = findChildren(config.steps, projStepId);
		if (children.length === 0) break;

		let nextStepId: number | undefined;
		if (children.length === 1) {
			nextStepId = children[0]?.id;
		} else {
			// Try to resolve branch using form values
			nextStepId = findNextStepIdSafe(config, projStepId, formValues);
			// If branch can't be resolved, follow first child as estimate
			if (nextStepId === undefined) {
				nextStepId = children[0]?.id;
			}
		}

		if (nextStepId === undefined) break;
		total += fieldsFromStepId(config, nextStepId).length;
		projStepId = nextStepId;
	}

	return total;
};

const findNextStepIdSafe = (
	config: WizardFormConfig,
	stepId: number,
	formValues: FormValues,
): number | undefined => {
	const childCandidates = findChildren(config.steps, stepId);
	if (childCandidates.length === 1) return childCandidates[0]?.id;

	for (const child of childCandidates) {
		const triggerChoice = child.triggerChoice;
		if (!triggerChoice) continue;
		const field = getFieldFromId(
			config,
			triggerChoice.stepId,
			triggerChoice.fieldId,
		);
		const fieldKey = getFieldSource(triggerChoice.stepId, field.identifier);
		if (
			formValues[fieldKey] === triggerChoice.choiceId &&
			field.type === "select"
		)
			return child.id;
	}
	return undefined;
};

export const countAnsweredFields = (
	config: WizardFormConfig,
	stepHistory: number[],
	currentFieldIndex: number,
): number => {
	let total = 0;

	// Sum fields for all steps in stepHistory except the current (last) one
	for (let i = 0; i < stepHistory.length - 1; i++) {
		const visitedStepId = stepHistory[i];
		if (visitedStepId === undefined) continue;
		total += fieldsFromStepId(config, visitedStepId).length;
	}

	// Add currentFieldIndex for the current step (0-indexed = number of completed fields)
	total += currentFieldIndex;

	return total;
};

export const isFileField = (field: Field): boolean => {
	return field.type === "file";
};

export const filterOutFileFieldValues = (
	config: Omit<WizardFormConfig, "configId">,
	formValues: FormValues,
): Record<string, string | number | undefined> => {
	const filtered: Record<string, string | number | undefined> = {};

	for (const [key, value] of Object.entries(formValues)) {
		try {
			const field = getFieldFromFieldSource(config, key);
			if (!isFileField(field)) {
				// Only include non-file fields
				if (
					typeof value === "string" ||
					typeof value === "number" ||
					value === undefined
				) {
					filtered[key] = value;
				}
			}
		} catch {
			// If we can't find the field, include the value anyway
			// This handles edge cases and maintains backward compatibility
			if (
				typeof value === "string" ||
				typeof value === "number" ||
				value === undefined
			) {
				filtered[key] = value;
			}
		}
	}

	return filtered;
};
